import { CareModel } from '../../models/user';

// values equal to Users.UserType backend Enum
export const USER_TYPES = {
  internalUser: "Internal user",
  trueUser: "True user",
  freeUser: "Free user",
};

export const PHASE_OPTIONS = [
  { label: "Blocks", value: "blocks"},
  { label: "Phase 0", value: "PhaseZero" },
  { label: "Phase 1 - Identification", value: "PhaseOne" },
  { label: "Phase 2 - Elimination", value: "PhaseTwo" },
  { label: "Phase 3 - Reintroduction", value: "PhaseThree" },
  { label: "Phase 4 - Application", value: "PhaseFour" },
];

export const CARE_MODEL_OPTIONS = [
  { label: CareModel.Collaborative, value: CareModel.Collaborative },
  { label: CareModel.CoachOnly, value: CareModel.CoachOnly },
  { label: CareModel.DigitalOnly, value: CareModel.DigitalOnly },
];

// values equal to ChartingNote.RiskStratification backend Enum
export const RISK_STRATIFICATION_OPTIONS = [
  { label: 'High', value: 'High' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Low', value: 'Low' },
];

export const PHASE_MAP = {
  PhaseZero:'Phase 0',
  PhaseOne: 'Phase 1',
  PhaseTwo: 'Phase 2',
  PhaseThree: 'Phase 3',
  PhaseFour: 'Phase 4',
  blocks: 'Blocks',
};

export const DEFAULT_AVATAR_IMG_URL = 'https://ayble-uploads-production.s3.amazonaws.com/avatars/coaches/default.png'

// values equal to the MeetingsFilter backend Enum
export type MeetingsFilterValue = 'today' | 'none' | 'past' | 'upcoming';

export type MeetingsPresetOption = {
  id: string;
  label: string;
  group: 'Upcoming' | 'Past';
  meetings: MeetingsFilterValue;
  meetingNumDays?: number;
};

export type MeetingConfirmationPresetOption = {
  id: string;
  label: string;
  group: 'Confirmations';
  confirmed: boolean;
};

export type MeetingsFilterOption = MeetingsPresetOption | MeetingConfirmationPresetOption;

// Presets for the Appointments sidebar filter. The Upcoming/Past presets each
// map to a single meetings/meeting_num_days combination, and the
// Confirmations presets map to `meeting_confirmed` - all params the
// /private/coach/users endpoint accepts. At most one preset per group can be
// active at a time.
export const MEETINGS_FILTER_OPTIONS: MeetingsFilterOption[] = [
  { id: 'today', label: 'Today', group: 'Upcoming', meetings: 'today' },
  { id: 'next7', label: 'Next 7 Days', group: 'Upcoming', meetings: 'upcoming', meetingNumDays: 7 },
  { id: 'next30', label: 'Next 30 Days', group: 'Upcoming', meetings: 'upcoming', meetingNumDays: 30 },
  { id: 'noneScheduled', label: 'None Scheduled', group: 'Upcoming', meetings: 'none' },
  { id: 'last7', label: 'Last 7 Days', group: 'Past', meetings: 'past', meetingNumDays: 7 },
  { id: 'last30', label: 'Last 30 Days', group: 'Past', meetings: 'past', meetingNumDays: 30 },
  { id: 'confirmed', label: 'Confirmed', group: 'Confirmations', confirmed: true },
  { id: 'unconfirmed', label: 'Unconfirmed', group: 'Confirmations', confirmed: false },
];

/** Column layout for the CareOS patient list — shared by header and rows. */
export const ROW_GRID = '2.4fr 2.2fr 1.4fr';
export const ROW_PADDING_X = 6;
export const ROW_HEIGHT = 88;

export const SORT_BY_OPTIONS = ['next_meeting', 'last_engaged'] as const;
export type SortBy = typeof SORT_BY_OPTIONS[number];

export const SORT_ORDER_OPTIONS = ['asc', 'desc'] as const;
export type SortOrder = typeof SORT_ORDER_OPTIONS[number];

/** Direction a column starts in when first clicked — most useful end first. */
export const INITIAL_SORT_ORDER_BY_COLUMN: Record<SortBy, SortOrder> = {
  next_meeting: 'asc', // soonest appointment first
  last_engaged: 'desc', // most recently engaged first
};

/** Columns with a `sortBy` are clickable and sort the list server-side. */
export const COLUMN_HEADERS: { label: string; sortBy?: SortBy }[] = [
  { label: 'Name' },
  { label: 'Next Appointment', sortBy: 'next_meeting' },
  { label: 'Last Engaged', sortBy: 'last_engaged' },
];


export const ENROLLMENT_STATUS_OPTIONS = [
  { id: 'new', label: 'New (last 7 days)' },
  { id: 'canceled', label: 'Cancelled' },
] as const;

export type EnrollmentStatus =
  (typeof ENROLLMENT_STATUS_OPTIONS)[number]['id'];

export const ONBOARDING_STATUS_OPTIONS = [
  { id: 'never_logged_in', label: 'Never Logged In' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'baseline_complete', label: 'Baseline Complete' },
] as const;

export type OnboardingStatus = typeof ONBOARDING_STATUS_OPTIONS[number]['id'];

export const INITIAL_VISIT_STATUS_OPTIONS = [
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'occurred', label: 'Occurred' },
] as const;

export type InitialVisitStatus =
  typeof INITIAL_VISIT_STATUS_OPTIONS[number]['id'];